import React from 'react';
import { Link } from 'react-router-dom';
import SeoLandingLayout from '../../components/SeoLandingLayout/SeoLandingLayout';
import {
  IA_BRAND_NAME,
  IA_CASOS,
  IA_HUB,
  IA_SERVICES,
  buildIaBreadcrumb,
  buildIaServiceSchema,
} from '../../config/iaBrand';

const SERVICE = IA_SERVICES.privada;

const FAQ = [
  {
    question: '¿En qué se diferencia de un agente conectado a ChatGPT u otra API?',
    answer:
      'A diferencia de los agentes IA conectados a APIs externas, aquí el modelo corre en tu propia infraestructura o en una nube privada europea dedicada. El dato no se envía a un proveedor genérico de modelos en la nube pública.',
  },
  {
    question: '¿Para quién es imprescindible?',
    answer:
      'Sector social, dependencia, sanidad, administración pública local y cualquier contrato con cláusula anti-cloud o categorías especiales de datos (menores, violencia de género, dependencia).',
  },
  {
    question: '¿Se integra con Vulpin?',
    answer:
      'Sí. La integración nativa con Vulpin Social / Care es parte del servicio: la IA trabaja sobre el expediente y los módulos que ya usáis, sin sacar el dato de vuestra red.',
  },
  {
    question: '¿El prototipo se puede desplegar en Azure si el dato sí puede salir?',
    answer:
      'Sí. Si no hay categoría especial ni cláusula anti-nube, el despliegue en producción (backend, API, Azure) está en IA en producción. Aquí nos quedamos cuando el expediente no puede ir a una API pública.',
  },
];

const IaPrivadaPage = ({ darkMode, toggleDarkMode }) => (
  <SeoLandingLayout
    darkMode={darkMode}
    toggleDarkMode={toggleDarkMode}
    title="IA que nunca sale de tu servidor — soberanía del dato | E-ASY"
    description="Despliegue de IA on-premise integrado con Vulpin para datos de menores, dependencia y violencia de género. El dato nunca sale de tu red."
    keywords="IA on-premise España, inteligencia artificial privada RGPD datos sensibles, IA soberana administración pública Canarias, IA para servicios sociales RGPD menores dependencia, IA sin nube pública"
    canonical={SERVICE.url}
    h1="IA privada: el dato nunca sale de tu servidor"
    lede="Para sector social, dependencia, sanidad y administración pública. Desplegamos el modelo en tu infraestructura o nube privada europea, integrado con Vulpin. Sin enviar datos sensibles a APIs genéricas."
    ctaTo="/contacto"
    ctaLabel="Solicita una prueba de concepto con tu propio caso"
    footerCtaLabel="Solicita una prueba de concepto con tu propio caso"
    faqItems={FAQ}
    serviceJsonLd={buildIaServiceSchema({
      name: 'IA privada / soberana on-premise',
      description:
        'Despliegue de modelos de IA en servidor propio o nube privada europea, integración con Vulpin Social/Care y auditoría RGPD para categorías especiales de datos.',
      url: SERVICE.url,
      serviceType: 'Inteligencia artificial on-premise y soberanía del dato',
    })}
    serviceJsonLdId="jsonld-service-ia-privada"
    cobertura
    breadcrumbJsonLd={buildIaBreadcrumb([
      { name: IA_HUB.label, url: IA_HUB.url },
      { name: SERVICE.label, url: SERVICE.url },
    ])}
  >
    <h2>El problema</h2>
    <p>
      Muchas “soluciones de IA” para entidades sociales o ayuntamientos son, en la práctica, un conector
      a una API en la nube pública. Eso choca con el RGPD cuando hay <strong>menores</strong>,{' '}
      <strong>dependencia</strong>, violencia de género u otras categorías especiales — y con cláusulas
      contractuales que prohíben sacar el dato fuera de la red del cliente.
    </p>
    <p>
      El riesgo no es solo legal: es de confianza. Familias, personas usuarias y administración necesitan
      saber dónde vive el expediente cuando “una IA lo resume”.
    </p>

    <h2>La solución</h2>
    <p>
      <strong>El dato nunca sale de tu servidor.</strong> En {IA_BRAND_NAME} desplegamos el modelo en
      infraestructura propia del cliente o en <strong>nube privada europea dedicada</strong>. A
      diferencia de los agentes IA conectados a APIs externas, aquí el modelo corre en tu propia
      infraestructura y se integra de forma nativa con <strong>Vulpin Social / Care</strong>.
    </p>
    <p>
      No es el mismo servicio que un agente de WhatsApp genérico (
      <Link to={IA_SERVICES.agentes.path}>agentes IA</Link>
      ) ni que{' '}
      <Link to={IA_SERVICES.produccion.path}>desplegar un prototipo en Azure</Link>
      : si el dato no puede salir, el modelo corre en vuestra red. Es soberanía del dato para quien no
      puede permitirse otra cosa.
    </p>

    <h2>Qué incluye</h2>
    <ul>
      <li>Despliegue del modelo en servidor propio del cliente o en nube privada europea dedicada</li>
      <li>Integración nativa con Vulpin Social / Care</li>
      <li>
        Auditoría RGPD específica para categorías especiales de datos (menores, violencia de género,
        dependencia)
      </li>
    </ul>

    <h2>Por qué con E-ASY</h2>
    <p>
      Conocemos el tercer sector y la operación diaria de entidades en Canarias: no partimos de un demo
      genérico. Si ya usáis Vulpin, la IA se apoya en módulos y flujos reales. Si todavía no, podemos
      combinar software de gestión e IA privada en un mismo plan.
    </p>
    <p>
      Mira las referencias de <Link to={IA_CASOS.path}>ACUFADE, AFFA y MPDL</Link> y el enfoque de{' '}
      <Link to="/tercer-sector">tercer sector</Link> y{' '}
      <Link to="/erp-para-ong">software de gestión para ONG</Link>.
    </p>

    <h2>Prueba social y casos</h2>
    <p>
      Trabajamos con entidades del ámbito social donde el dato sensible es el núcleo del servicio, no un
      “extra”. En <Link to={IA_CASOS.path}>{IA_CASOS.label}</Link> resumimos ACUFADE, AFFA (Alzheimer Fuerteventura) y
      MPDL: sector, problema y módulo Vulpin.
    </p>

    <h2>Siguiente paso</h2>
    <p>
      Una prueba de concepto con vuestro propio caso (un tipo de documento, un flujo de expediente, un
      resumen controlado) dice más que cualquier slide. Sin comprometer el resto de la organización.
    </p>
    <p>
      <Link to="/contacto" className="seo-landing-cta">
        Solicita una prueba de concepto con tu propio caso
      </Link>
    </p>
    <p>
      Para cumplir el marco de uso de IA en el personal:{' '}
      <Link to={IA_SERVICES.cumplimiento.path}>{IA_SERVICES.cumplimiento.label}</Link>. Lecturas:{' '}
      <Link to={`/blog/${SERVICE.blogSlug}`}>
        Dónde desplegar un agente de IA: Azure, on-premise o API pública
      </Link>
      {' · '}
      <Link to="/blog/datos-menores-dependencia-rgpd-ia">
        Por qué los datos de menores y dependencia no pueden ir a una API genérica
      </Link>
      {' · '}
      <Link to="/blog/coste-ia-privada-2026">
        Cuánto cuesta montar IA privada en 2026
      </Link>
      .
    </p>
  </SeoLandingLayout>
);

export default IaPrivadaPage;
